import React, { useMemo } from "react";
import { Card, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { Booking } from "../../types/booking";
import { AGENT_ROUTES } from "../../config/agentPortal";
import {
  AgentContainerProductRow,
  getContainerProductTotals,
  getContainerProductsFromBookings,
} from "../../utils/agentContainerProducts";
import type { AgentContainerExportFormat } from "../../utils/agentContainerExport";
import {
  agentCompactCardStyles,
  agentCompactTableEvenClass,
  spreadColumnsEvenly,
} from "./agentCompactTable";
import { AgentContainerTableSummary } from "./AgentContainerTableSummary";
import { AgentContainerExportDropdown } from "./AgentContainerExportDropdown";

interface AgentContainerProductsTableProps {
  bookings: Booking[];
  containerReference?: string | null;
  loading?: boolean;
  compact?: boolean;
  onExport?: (format: AgentContainerExportFormat) => Promise<void>;
}

const formatNumber = (value: number | null | undefined): string =>
  value != null ? value.toLocaleString() : "-";

export const AgentContainerProductsTable: React.FC<
  AgentContainerProductsTableProps
> = ({
  bookings,
  containerReference,
  loading = false,
  compact = false,
  onExport,
}) => {
  const navigate = useNavigate();

  const products = useMemo(
    () => getContainerProductsFromBookings(bookings),
    [bookings]
  );

  const totals = useMemo(
    () => getContainerProductTotals(bookings),
    [bookings]
  );

  const columns: ColumnsType<AgentContainerProductRow> = compact
    ? spreadColumnsEvenly([
        {
          title: "EAN",
          dataIndex: "ean",
          key: "ean",
          render: (value: string | null) => value || "-",
        },
        {
          title: "Name",
          dataIndex: "name",
          key: "name",
          render: (value: string | null) => value || "-",
        },
        {
          title: "Booking",
          dataIndex: "booking_id",
          key: "booking_id",
          render: (bookingId: number) => (
            <a
              onClick={(e) => {
                e.preventDefault();
                navigate(AGENT_ROUTES.bookingDetail(bookingId));
              }}
              href={AGENT_ROUTES.bookingDetail(bookingId)}
            >
              {bookingId}
            </a>
          ),
        },
        {
          title: "Ctn",
          dataIndex: "cartons",
          key: "cartons",
          render: (value: number | null) => formatNumber(value),
        },
        {
          title: "CBM",
          dataIndex: "cbm",
          key: "cbm",
          render: (value: number | null) =>
            value != null
              ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : "-",
        },
        {
          title: "Kg",
          dataIndex: "gross_weight",
          key: "gross_weight",
          render: (value: number | null) => formatNumber(value),
        },
        {
          title: "Pcs",
          dataIndex: "pieces",
          key: "pieces",
          render: (value: number | null) => formatNumber(value),
        },
        {
          title: "HS",
          dataIndex: "hs_code",
          key: "hs_code",
          render: (value: string | null) => value || "-",
        },
      ])
    : [
        {
          title: "EAN",
          dataIndex: "ean",
          key: "ean",
          width: 120,
          ellipsis: true,
          render: (value: string | null) => value || "-",
        },
        {
          title: "Name",
          dataIndex: "name",
          key: "name",
          ellipsis: true,
        },
        {
          title: "Booking",
          dataIndex: "booking_id",
          key: "booking_id",
          width: 100,
          render: (bookingId: number) => (
            <a
              onClick={(e) => {
                e.preventDefault();
                navigate(AGENT_ROUTES.bookingDetail(bookingId));
              }}
              href={AGENT_ROUTES.bookingDetail(bookingId)}
            >
              {bookingId}
            </a>
          ),
        },
        {
          title: "Ctn",
          dataIndex: "cartons",
          key: "cartons",
          width: 80,
          render: (value: number | null) => formatNumber(value),
        },
        {
          title: "CBM",
          dataIndex: "cbm",
          key: "cbm",
          width: 80,
          render: (value: number | null) =>
            value != null
              ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : "-",
        },
        {
          title: "Kg",
          dataIndex: "gross_weight",
          key: "gross_weight",
          width: 110,
          render: (value: number | null) => formatNumber(value),
        },
        {
          title: "Pcs",
          dataIndex: "pieces",
          key: "pieces",
          width: 90,
          render: (value: number | null) => formatNumber(value),
        },
        {
          title: "HS",
          dataIndex: "hs_code",
          key: "hs_code",
          width: 120,
          ellipsis: true,
          render: (value: string | null) => value || "-",
        },
      ];

  const title = containerReference
    ? `Products in ${containerReference}`
    : "Products";

  return (
    <Card
      size="small"
      styles={compact ? agentCompactCardStyles : undefined}
      title={title}
      extra={
        onExport ? (
          <AgentContainerExportDropdown
            disabled={products.length === 0}
            onExport={onExport}
          />
        ) : null
      }
    >
      <AgentContainerTableSummary
        items={[
          { value: totals.products, label: "products" },
          { value: totals.cartons.toLocaleString(), label: "ctn" },
          {
            value: totals.cbm.toLocaleString(undefined, {
              maximumFractionDigits: 3,
            }),
            label: "CBM",
          },
          {
            value: totals.grossWeightKg.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            }),
            label: "kg",
          },
        ]}
      />
      <Table
        rowKey="id"
        size="small"
        className={compact ? agentCompactTableEvenClass : undefined}
        columns={columns}
        dataSource={products}
        loading={loading}
        pagination={false}
        tableLayout="fixed"
        locale={{ emptyText: "No products in this container" }}
      />
    </Card>
  );
};
